import { EXTRACT_OCR_STATUS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { useActivityContext } from "core/model/utils/browser/ActivitySingleton";
import { getErrorMessage } from "core/model/utils/errors";
import { ExtractOCRStatus, ExtractOCRStatusResponse } from "core/types";
// TODO: move toast usage out of core or inject from app
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "translations";

const ACTIVE_POLL_INTERVAL = 3_000;
const INACTIVE_POLL_INTERVAL = 120_000;

export const useExtractOCRStatus = ({
  auctionId,
  careseekerId,
  consultationId,
  externalId,
  patientId,
  skip,
}: {
  consultationId: number | undefined;
  careseekerId: number | undefined;
  patientId: number;
  auctionId: number | undefined;
  skip: boolean;
  externalId: string | undefined;
}) => {
  const [result, setResult] = useState<ExtractOCRStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const translations = useTranslations();
  const [isInitialResponseReceived, setIsInitialResponseReceived] =
    useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const { active } = useActivityContext();
  const previousResultRef = useRef<ExtractOCRStatusResponse | null>(null);

  const isAllProcessed = useMemo(() => {
    const documents = result?.documents;
    if (!documents?.length) {
      return false;
    }

    return documents.every((document) =>
      (
        [
          EXTRACT_OCR_STATUS.EXTRACTED,
          EXTRACT_OCR_STATUS.FAILED,
          EXTRACT_OCR_STATUS.DELETED,
        ] as ExtractOCRStatus[]
      ).includes(document.status),
    );
  }, [result]);

  const pollInterval = useMemo(
    () => (active ? ACTIVE_POLL_INTERVAL : INACTIVE_POLL_INTERVAL),
    [active],
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const fetchStatus = useCallback(
    async (isInitialLoad = false) => {
      try {
        if (skip) {
          return null;
        }

        if (!careseekerId || !patientId) {
          throw new Error(
            "careseekerId, patientId or auctionId is not defined",
          );
        }

        if (!auctionId && !consultationId) {
          throw new Error("auctionId or consultationId is not defined");
        }

        const ocrResult = await aiApi.extract.ocrStatus({
          auctionId,
          careseekerId,
          consultationId,
          externalId,
          patientId,
        });

        if (!isMountedRef.current) return null;

        if (isInitialLoad) {
          setIsInitialResponseReceived(true);
        } else {
          const prevFailedExtractions =
            previousResultRef.current?.documents.filter(
              (document) => document.status === EXTRACT_OCR_STATUS.FAILED,
            ) ?? [];

          const failedExtractions = ocrResult.documents.filter(
            (document) => document.status === EXTRACT_OCR_STATUS.FAILED,
          );
          const newFailedExtractions = failedExtractions.filter(
            (document) =>
              !prevFailedExtractions.some(
                (prevDocument) =>
                  prevDocument.recare_file_id === document.recare_file_id,
              ),
          );

          newFailedExtractions.forEach((document) => {
            toast({
              color: "danger",
              message:
                translations.extractDocs.filesTabChanges.errorExtractBannerTitle(
                  { filename: document.filename },
                ),
            });
          });
        }

        previousResultRef.current = ocrResult;

        setResult(ocrResult);
        setError(null);
        return ocrResult;
      } catch (err) {
        if (!isMountedRef.current) return null;
        const message = getErrorMessage(err);
        console.error("error with extract ocr status", message);
        setError(message);
        return null;
      }
    },
    [auctionId, careseekerId, consultationId, patientId, skip, stopPolling],
  );

  useEffect(() => {
    if (skip || (isAllProcessed && !error)) {
      return;
    }

    fetchStatus(true);

    const intervalId = setInterval(() => {
      fetchStatus();
    }, pollInterval);

    intervalRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
    };
  }, [error, fetchStatus, isAllProcessed, pollInterval, skip]);

  const refetch = useCallback(async () => {
    return fetchStatus();
  }, [fetchStatus]);

  return { error, isInitialResponseReceived, refetch, result };
};
